import {
  isGroup,
  STORE_TIMEZONE,
  type Filter,
  type FilterCondition,
  type FilterNode,
  type Json,
  type Page,
  type Sort,
} from "@estelle/domain";

/**
 * Recent and Search both show rows built on the client — see the file
 * comment on `useDocsTree` for why `DocsRepository` has no single call that
 * returns them ready-made. Once a row set exists locally, filtering,
 * sorting and paging it is exactly what the fixture adapter's own
 * `runQuery` does for a real repository list, so this mirrors that engine
 * rather than leaving Recent and Search as the two lists in Docs that
 * ignore the filter bar — see CLAUDE.md's "Filters and saved views are
 * foundation" invariant. `modules/orders/localQuery.ts` documents the same
 * trade-off for order history and cannot be imported from here: a module
 * depends on `@estelle/domain`, never on another module's internals.
 */

type Projection = Record<string, unknown>;

function asNumber(value: unknown): number | null {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

function compareValues(left: unknown, right: Json | undefined): number | null {
  const leftNumber = asNumber(left);
  const rightNumber = asNumber(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }

  const leftText = asText(left);
  const rightText = asText(right);
  if (leftText === "" || rightText === "") return null;
  return leftText < rightText ? -1 : leftText > rightText ? 1 : 0;
}

function asPair(value: Json | undefined): [Json | undefined, Json | undefined] {
  if (!Array.isArray(value)) return [undefined, undefined];
  return [value[0], value[1]];
}

/**
 * Wall-clock time, not the fixture adapter's pinned clock — that constant is
 * an adapter internal this client-side engine must not reach for (see the
 * file comment). Harmless here: `in_last_days` on a "when did this page
 * last change" column only disagrees with the rest of the app by the gap
 * between real now and the fixture's frozen instant, and both land on the
 * same day.
 */
function daysBetween(value: unknown, now: number): number | null {
  const text = asText(value);
  if (text === "") return null;
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : (now - parsed) / 86_400_000;
}

/**
 * `"2026-09-09"` — the calendar date `ms` falls on in the store's zone.
 * Mirrors `lib/filter-predicate.ts`'s helper of the same name (#406);
 * duplicated rather than imported for the reason in the file comment above —
 * that helper is private to its file, and this engine is self-contained.
 */
function calendarDateKey(ms: number): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: STORE_TIMEZONE }).format(
    new Date(ms),
  );
}

function matchesCondition(
  projection: Projection,
  condition: FilterCondition,
  now: number,
): boolean {
  const actual = projection[condition.field];
  const expected = condition.value;

  switch (condition.operator) {
    case "is_empty":
      return isEmptyValue(actual);
    case "is_not_empty":
      return !isEmptyValue(actual);

    case "is":
      return asText(actual).toLowerCase() === asText(expected).toLowerCase();
    case "is_not":
      return asText(actual).toLowerCase() !== asText(expected).toLowerCase();
    case "contains":
      return asText(actual)
        .toLowerCase()
        .includes(asText(expected).toLowerCase());
    case "not_contains":
      return !asText(actual)
        .toLowerCase()
        .includes(asText(expected).toLowerCase());
    case "starts_with":
      return asText(actual)
        .toLowerCase()
        .startsWith(asText(expected).toLowerCase());
    case "ends_with":
      return asText(actual)
        .toLowerCase()
        .endsWith(asText(expected).toLowerCase());

    case "eq":
      return compareValues(actual, expected) === 0;
    case "neq":
      return compareValues(actual, expected) !== 0;
    case "lt": {
      const result = compareValues(actual, expected);
      return result !== null && result < 0;
    }
    case "lte": {
      const result = compareValues(actual, expected);
      return result !== null && result <= 0;
    }
    case "gt": {
      const result = compareValues(actual, expected);
      return result !== null && result > 0;
    }
    case "gte": {
      const result = compareValues(actual, expected);
      return result !== null && result >= 0;
    }
    case "between": {
      const [low, high] = asPair(expected);
      const lower = compareValues(actual, low);
      const upper = compareValues(actual, high);
      return lower !== null && upper !== null && lower >= 0 && upper <= 0;
    }

    case "in_last_days": {
      const age = daysBetween(actual, now);
      const window = asNumber(expected);
      return age !== null && window !== null && age >= 0 && age <= window;
    }
    case "in_next_days": {
      const age = daysBetween(actual, now);
      const window = asNumber(expected);
      return age !== null && window !== null && age < 0 && -age <= window;
    }

    case "is_today": {
      const text = asText(actual);
      const parsed = text === "" ? NaN : Date.parse(text);
      return (
        !Number.isNaN(parsed) &&
        calendarDateKey(parsed) === calendarDateKey(now)
      );
    }
    case "in_last_calendar_days": {
      const text = asText(actual);
      const parsed = text === "" ? NaN : Date.parse(text);
      const window = asNumber(expected);
      if (Number.isNaN(parsed) || window === null || window < 1) return false;
      const rowKey = calendarDateKey(parsed);
      const todayKey = calendarDateKey(now);
      const startKey = calendarDateKey(now - (window - 1) * 86_400_000);
      return rowKey >= startKey && rowKey <= todayKey;
    }

    // Enum, reference, tag and boolean operators — none of Docs' declared
    // fields use these types today, so they fall through to "no match"
    // rather than a `default` that would hide a real gap silently.
    case "is_any_of":
    case "is_none_of":
    case "has_tag":
    case "not_has_tag":
    case "has_any_tag":
    case "is_true":
    case "is_false":
      return false;
  }
}

function matchesNode(
  projection: Projection,
  node: FilterNode,
  now: number,
): boolean {
  if (!isGroup(node)) return matchesCondition(projection, node, now);
  if (node.children.length === 0) return true;

  return node.combinator === "and"
    ? node.children.every((child) => matchesNode(projection, child, now))
    : node.children.some((child) => matchesNode(projection, child, now));
}

function asSortableText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function compareForSort(left: unknown, right: unknown): number {
  const leftNumber = typeof left === "string" ? Number(left) : left;
  const rightNumber = typeof right === "string" ? Number(right) : right;

  if (
    typeof leftNumber === "number" &&
    typeof rightNumber === "number" &&
    !Number.isNaN(leftNumber) &&
    !Number.isNaN(rightNumber)
  ) {
    return leftNumber - rightNumber;
  }

  return asSortableText(left).localeCompare(asSortableText(right));
}

/**
 * Filter, sort and cursor-page a row set already assembled on the client —
 * see the file comment for why Recent and Search need this instead of
 * calling a repository list directly.
 */
export function runLocalQuery<T>(
  items: readonly T[],
  query: { filter: Filter; sort: Sort[]; limit: number; cursor?: string },
  options: {
    project: (item: T) => Projection;
    identify: (item: T) => string;
    /** Applied when the caller's own sort is empty. */
    defaultSort?: Sort[];
  },
): Page<T> {
  const { project, identify, defaultSort } = options;
  const now = Date.now();

  const rows = items
    .map((item) => ({ item, projection: project(item) }))
    .filter((row) => matchesNode(row.projection, query.filter, now));

  const sort = query.sort.length > 0 ? query.sort : (defaultSort ?? []);
  if (sort.length > 0) {
    rows.sort((a, b) => {
      for (const { column, direction } of sort) {
        const result = compareForSort(
          a.projection[column],
          b.projection[column],
        );
        if (result !== 0) return direction === "asc" ? result : -result;
      }
      return 0;
    });
  }

  const limit = Math.max(query.limit, 1);
  let start = 0;
  if (query.cursor) {
    const index = rows.findIndex((row) => identify(row.item) === query.cursor);
    start = index === -1 ? 0 : index + 1;
  }

  const page = rows.slice(start, start + limit);
  const last = page[page.length - 1];
  const hasMore = start + page.length < rows.length;

  return {
    items: page.map((row) => row.item),
    nextCursor: hasMore && last ? identify(last.item) : null,
    total: rows.length,
  };
}
